import cls from 'classnames';
import invert from 'lodash/invert';
import * as React from 'react';
import { Flipped, spring } from 'react-flip-toolkit';
import { EllipsisTooltip, Tooltip } from 'ssc-ui-react';

import { ReactComponent as TvIcon } from '@/assets/svg/tv.svg';
import { $gt } from '@/common/appCtx';
import { useWorkforceEnums } from '@/store/workforce-enums';

import { transformNum } from '../../utils/common';
import Empty from '../empty';
import styles from './index.module.less';

export interface ThroughputItem {
  activity_type?: number;
  throughput?: number;
  is_calculate_type?: boolean;
}

export interface GroupProps {
  selected: boolean;
  title: string;
  orderCount: number;
  workstationCount: number;
  operatorCount: number;
  interactiveTypeThroughputList: ThroughputItem[];
  nonInteractiveTypeThroughputList: ThroughputItem[];
  onSelected: () => void;
  onTvMode: () => void;
}

interface ThroughputSectionProps {
  title: string;
  items: ThroughputItem[];
  workstationTypeMap: Record<string, string>;
  className?: string;
}

const ThroughputSection: React.FC<ThroughputSectionProps> = ({ title, items, workstationTypeMap, className }) => (
  <div className={className}>
    <div className={styles.scanTitle}>{title}</div>
    <div className={styles.scanContent}>
      {items.map((item, index) => (
        <div
          key={index}
          className={cls(styles.line, { [styles.bold]: item.is_calculate_type })}
        >
          <Tooltip
            title={workstationTypeMap[item.activity_type ?? '']}
          >
            <div className={styles.left}>
              {workstationTypeMap[item.activity_type ?? '']}
            </div>
          </Tooltip>
          <span className={styles.right}>{transformNum(item.throughput || 0)}</span>
        </div>
      ))}
      {!items.length && (
        <div className={styles.empty}>
          <Empty style={{ width: 56, height: 56 }} />
        </div>
      )}
    </div>
  </div>
);

const Group: React.FC<GroupProps> = (props) => {
  const enums = useWorkforceEnums.use.enums();
  const workstationTypeMap = React.useMemo(() => invert(enums['workstation_type']), [enums]);

  const onElementAppear = (el: HTMLElement, index: number) =>
    spring({
      onUpdate: val => {
        // @ts-ignore
        el.style.opacity = val;
      },
      delay: index * 50
    });

  const onExit = (el: HTMLElement, index: number, removeElement: () => void) => {
    spring({
      config: { overshootClamping: true },
      onUpdate: (val) => {
        const value = 1 - (val as number);
        el.style.transform = `scaleX(${value})`;
      },
      delay: index * 50,
      onComplete: removeElement
    });

    return () => {
      el.style.opacity = '';
      removeElement();
    };
  };

  return (
    <Flipped
      key={props.title}
      flipId={props.title}
      onAppear={onElementAppear}
      onExit={onExit}
      stagger={true}
    >
      <div
        className={styles.group}
        onClick={props.onSelected}
      >
        {props.selected && <div className={styles.highlight} />}
        <div className={styles.head}>
          <EllipsisTooltip lines={1} title={props.title}>
            <span className={styles.title}>{props.title}</span>
          </EllipsisTooltip>
          <div className={styles.icon} onClick={props.onTvMode}><TvIcon /></div>
        </div>
        <div className={styles.order}>
          <span className={styles.orderCount}>{transformNum(props.orderCount)}</span>
          <span>{$gt('Orders')}</span>
        </div>
        <div className={styles.workstations}>
          <span>{$gt('Workstations')}: {transformNum(props.workstationCount)}</span>
          <span>{$gt('Operators')}: {transformNum(props.operatorCount)}</span>
        </div>
        <div className={styles.horizontalDivider}></div>
        <div className={styles.scan}>
          <ThroughputSection
            className={styles.scanLeft}
            title={$gt('Scan')}
            items={props.interactiveTypeThroughputList}
            workstationTypeMap={workstationTypeMap}
          />
          <div className={styles.verticalDivider}></div>
          <ThroughputSection
            className={styles.scanRight}
            title={$gt('No Scan')}
            items={props.nonInteractiveTypeThroughputList}
            workstationTypeMap={workstationTypeMap}
          />
        </div>
      </div>
    </Flipped>
  );
};

export default Group;
